import * as React from 'react';
import { useState } from 'react';
import { getCommunityActivityLogs, type ActivityLog } from '@/api/communities';
import { Avatar, Box, Chip, Paper, Typography, useTheme } from '@mui/material';
import { FileText as FileTextIcon, House as HouseIcon, User as UserIcon } from '@phosphor-icons/react';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { MaterialReactTable, type MRT_ColumnDef } from 'material-react-table';

import { useUser } from '@/hooks/v2/use-user';

dayjs.extend(relativeTime);

// Helper function to get the appropriate icon for subject type
const getSubjectIcon = (subjectType: string) => {
  switch (subjectType) {
    case 'User':
      return <UserIcon size={16} />;
    case 'Unit':
      return <HouseIcon size={16} />;
    default:
      return <FileTextIcon size={16} />;
  }
};

export default function CommunityActivityPage(): React.JSX.Element {
  const { community, isLoading: isLoadingUser } = useUser();
  const theme = useTheme();
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage] = useState(20);

  // Table columns configuration
  const columns = React.useMemo<MRT_ColumnDef<ActivityLog>[]>(
    () => [
      {
        accessorKey: 'description',
        header: 'Activity',
        size: 300,
        accessorFn: (row) => row.description,
        enableSorting: true,
        enableColumnFilter: true,
        filterVariant: 'text',
        filterPlaceholder: 'Search activities...',
        Cell: ({ row }) => (
          <Box>
            <Typography sx={{ fontWeight: 500, color: 'text.primary' }} variant="body2">
              {row.original.description}
            </Typography>
            <Typography sx={{ color: 'text.secondary' }} variant="caption">
              {dayjs(row.original.created_at).fromNow()}
            </Typography>
          </Box>
        ),
      },
      {
        accessorKey: 'causer',
        header: 'User',
        size: 200,
        accessorFn: (row) => row.causer?.name || 'System',
        enableSorting: true,
        enableColumnFilter: true,
        filterVariant: 'text',
        filterPlaceholder: 'Search users...',
        Cell: ({ row }) => {
          const causer = row.original.causer;
          if (!causer) {
            return (
              <Typography sx={{ color: 'text.secondary' }} variant="body2">
                System
              </Typography>
            );
          }
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar sx={{ width: 24, height: 24, fontSize: 12 }}>
                {causer.name
                  .split(' ')
                  .map((n) => n[0])
                  .join('')
                  .toUpperCase()}
              </Avatar>
              <Typography sx={{ color: 'text.primary' }} variant="body2">
                {causer.name}
              </Typography>
            </Box>
          );
        },
      },
      {
        accessorKey: 'subject',
        header: 'Subject',
        size: 200,
        accessorFn: (row) => row.subject?.name || 'Unknown',
        enableSorting: true,
        enableColumnFilter: true,
        filterVariant: 'text',
        filterPlaceholder: 'Search subjects...',
        Cell: ({ row }) => {
          const subject = row.original.subject;
          if (!subject) {
            return (
              <Typography sx={{ color: 'text.secondary' }} variant="body2">
                Unknown
              </Typography>
            );
          }
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {getSubjectIcon(subject.type)}
              <Chip label={subject.type} size="small" sx={{ fontSize: '0.75rem', mr: 1 }} />
              <Typography sx={{ color: 'text.primary' }} variant="body2">
                {subject.name}
              </Typography>
            </Box>
          );
        },
      },
      {
        accessorKey: 'log_name',
        header: 'Category',
        size: 150,
        accessorFn: (row) => row.log_name || 'default',
        enableSorting: true,
        enableColumnFilter: true,
        filterVariant: 'select',
        Cell: ({ row }) => (
          <Chip
            label={row.original.log_name || 'default'}
            size="small"
            sx={{ textTransform: 'capitalize' }}
            variant="outlined"
          />
        ),
      },
      {
        accessorKey: 'created_at',
        header: 'Date & Time',
        size: 180,
        accessorFn: (row) => dayjs(row.created_at).format('MMM D, YYYY h:mm A'),
        enableSorting: true,
        Cell: ({ row }) => (
          <Box>
            <Typography sx={{ color: 'text.primary' }} variant="body2">
              {dayjs(row.original.created_at).format('MMM D, YYYY')}
            </Typography>
            <Typography sx={{ color: 'text.secondary' }} variant="caption">
              {dayjs(row.original.created_at).format('h:mm A')}
            </Typography>
          </Box>
        ),
      },
    ],
    []
  );

  const { data, isLoading, error } = useQuery({
    queryKey: [`${community?.id}-activity`, currentPage, rowsPerPage],
    queryFn: () =>
      getCommunityActivityLogs(community!.id, {
        page: currentPage,
        per_page: rowsPerPage,
        sort: '-created_at',
      }),
    enabled: Boolean(community?.id),
  });

  const activities = data?.activities || [];
  const meta = data?.meta;

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  if (isLoading || isLoadingUser) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Typography sx={{ color: 'text.secondary' }} variant="body1">
          Loading activity logs...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography sx={{ color: 'error.main', mb: 2 }} variant="h6">
          Failed to load activity logs
        </Typography>
        <Typography sx={{ color: 'text.secondary' }} variant="body2">
          {error instanceof Error ? error.message : 'An unknown error occurred'}
        </Typography>
      </Box>
    );
  }

  return (
    <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
      <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Activity Log ({activities.length})</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Here you can view all activity and changes within your community.</Typography>
      </Box>
      {activities.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <FileTextIcon color={theme.palette.text.secondary} size={48} />
          <Typography sx={{ color: 'text.secondary', mt: 2 }} variant="h6">
            No activity logs found
          </Typography>
          <Typography sx={{ color: 'text.secondary' }} variant="body2">
            Activity logs will appear here as users interact with the system.
          </Typography>
        </Box>
      ) : (
        <MaterialReactTable
          columns={columns}
          data={activities}
          enableColumnFilters
          enableColumnResizing
          enableDensityToggle
          enableFullScreenToggle
          enablePagination={false}
          enableRowActions={false}
          enableSorting
          initialState={{
            showGlobalFilter: true,
            showColumnFilters: false,
            columnPinning: {
              left: [],
              right: [],
            },
            sorting: [{ id: 'created_at', desc: true }],
          }}
          muiSearchTextFieldProps={{
            size: 'small',
            variant: 'outlined',
            placeholder: 'Search all activities...',
          }}
          muiTableBodyCellProps={{
            sx: {
              borderBottom: '1px solid',
              borderColor: 'divider',
            },
          }}
          muiTableContainerProps={{
            sx: {
              minHeight: '500px',
            },
          }}
          muiTableHeadCellProps={{
            sx: {
              backgroundColor: 'background.paper',
              fontWeight: 600,
              fontSize: '0.875rem',
            },
          }}
          muiTableProps={{
            sx: {
              tableLayout: 'fixed',
            },
          }}
          state={{
            isLoading,
          }}
        />
      )}
    </Paper>
  );
}
